import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { customKeys } from "@/lib/queryKeyFactory";
import { notificationService } from "../services";
import { NOTIFICATION_MESSAGES, NOTIFICATION_POLLING, markedReadMessage } from "../constants";
import type { NotificationFilters } from "../types";

/**
 * Paged inbox list. Pass `unreadOnly` to filter, `page`/`size` to paginate.
 * Keeps previous page visible while the next loads. `options.enabled` lets
 * callers defer the fetch (e.g. the bell only loads its preview when open).
 */
export function useNotifications(filters?: NotificationFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customKeys.notifications.list(filters),
    queryFn: () => notificationService.getNotifications(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Unread badge count. Poll-driven (no realtime push): refetches on an interval
 * while the tab is visible (paused when backgrounded) and on window focus.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: customKeys.notifications.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    select: (data) => data.unreadCount,
    refetchInterval: NOTIFICATION_POLLING.UNREAD_COUNT_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: NOTIFICATION_POLLING.UNREAD_COUNT_STALE_MS,
  });
}

/** Single notification (ownership-checked). */
export function useNotification(id: string) {
  return useQuery({
    queryKey: customKeys.notifications.detail(id),
    queryFn: () => notificationService.getNotification(id),
    enabled: !!id,
  });
}

/** Mark a single notification read. Silent on success; refreshes list + badge. */
export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: customKeys.notifications.lists() });
      void queryClient.invalidateQueries({ queryKey: customKeys.notifications.unreadCount() });
      void queryClient.invalidateQueries({ queryKey: customKeys.notifications.detail(id) });
    },
  });
}

/** Mark all read; toasts the count and clears the badge. */
export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: (count) => {
      toast.success(markedReadMessage(count));
      void queryClient.invalidateQueries({ queryKey: customKeys.notifications.lists() });
      void queryClient.invalidateQueries({ queryKey: customKeys.notifications.unreadCount() });
    },
  });
}

/** Soft-delete a notification; refreshes list + badge. */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.deleteNotification(id),
    onSuccess: () => {
      toast.success(NOTIFICATION_MESSAGES.DELETE_SUCCESS);
      void queryClient.invalidateQueries({ queryKey: customKeys.notifications.lists() });
      void queryClient.invalidateQueries({ queryKey: customKeys.notifications.unreadCount() });
    },
  });
}
